import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  HostListener,
  input,
  output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, Router } from '@angular/router';
import { BREADCRUMBS, CURRENT_USER, Crumb } from '../nav-data';
import { AuthService } from '../../shared/auth.service';
import { AppNotification, NotificationsService } from '../../shared/notifications.service';
import { formatTimeAgo } from '../../shared/format';

/**
 * Reusable top bar: mobile menu toggle, route-driven breadcrumb, global search,
 * notifications bell (live unread badge + dropdown) and the current-user chip
 * with Profile/Logout dropdown.
 */
@Component({
  selector: 'app-navbar',
  imports: [RouterLink],
  templateUrl: './navbar.html',
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Navbar {
  readonly activeKey = input<string>('');
  readonly toggleSidebar = output<void>();

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly notifications = inject(NotificationsService);

  protected readonly user = computed(() => this.authService.currentUser() ?? CURRENT_USER);
  protected readonly crumbs = computed<Crumb[]>(
    () => BREADCRUMBS[this.activeKey()] ?? [['Home', 'dashboard']],
  );
  protected readonly dropdownOpen = signal(false);
  protected readonly bellOpen = signal(false);
  protected readonly timeAgo = formatTimeAgo;

  constructor() {
    this.notifications.startPolling();
    this.destroyRef.onDestroy(() => this.notifications.stopPolling());
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.dropdownOpen.set(false);
    this.bellOpen.set(false);
  }

  protected toggleDropdown(): void {
    this.bellOpen.set(false);
    this.dropdownOpen.update((v) => !v);
  }

  protected closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  protected toggleBell(): void {
    this.dropdownOpen.set(false);
    const opening = !this.bellOpen();
    this.bellOpen.set(opening);
    if (opening) {
      this.notifications
        .loadRecent()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      this.notifications
        .refreshUnreadCount()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  protected closeBell(): void {
    this.bellOpen.set(false);
  }

  /** Row click: mark read (if unread) then navigate to its link, if any. */
  protected openNotification(notification: AppNotification): void {
    if (!notification.readAt) {
      // Best-effort: navigation isn't chained off this, and there's no error UI
      // in the dropdown to surface a failure to — a noop error handler just
      // keeps a failed mark-read from becoming an unhandled/global error.
      this.notifications
        .markRead(notification.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ error: () => {} });
    }
    this.closeBell();
    if (notification.link) {
      void this.router.navigateByUrl(notification.link);
    }
  }

  /** Explicit per-item "mark read" control — stays in the dropdown, doesn't navigate. */
  protected markNotificationRead(notification: AppNotification, event: Event): void {
    event.stopPropagation();
    if (notification.readAt) return;
    this.notifications
      .markRead(notification.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => {} });
  }

  logout(): void {
    this.dropdownOpen.set(false);
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
